import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { FiSend } from "react-icons/fi";
import useMentionAutocomplete from "../../hooks/useMentionAutocomplete";
import MentionDropdown from "../common/MentionDropdown";
import "./CommentInput.css";

const CommentInput = ({
  onSubmit,
  placeholder = "Write a comment...",
  buttonText = "Post",
}) => {
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef(null);

  // @mention autocomplete
  const {
    suggestions,
    showDropdown,
    loading,
    handleInputChange,
    insertMention,
    closeDropdown,
    resetMentions,
  } = useMentionAutocomplete();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim()) {
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(content.trim());
      setContent("");
      resetMentions();
    } catch (error) {
      // Silent fail - error handling done by parent
    } finally {
      setSubmitting(false);
    }
  };

  const handleTextChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    
    // Handle @mention detection
    const cursorPosition = e.target.selectionStart;
    handleInputChange(newContent, cursorPosition);
  };

  const handleMentionSelect = (user) => {
    const cursorPosition = textareaRef.current?.selectionStart || content.length;
    const result = insertMention(user, content, cursorPosition);
    
    if (result) {
      setContent(result.text);
      
      // Set cursor position after the inserted mention
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(result.cursorPosition, result.cursorPosition);
        }
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    // Don't submit on Enter when dropdown is open
    if (showDropdown && (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === "Tab")) {
      // Let MentionDropdown handle these keys
      return;
    }
    
    // Submit on Ctrl+Enter or Cmd+Enter
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit(e);
    }
  };

  const getCountClass = () => {
    if (content.length >= 1000) return "cbx-comment-input__count--limit";
    if (content.length >= 900) return "cbx-comment-input__count--warning";
    return "";
  };

  return (
    <form onSubmit={handleSubmit} className="cbx-comment-input">
      <div className="cbx-comment-input__wrapper">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={2}
          maxLength={1000}
          disabled={submitting}
          className="cbx-comment-input__textarea"
        />
        
        {/* @mention dropdown */}
        {showDropdown && (
          <MentionDropdown
            suggestions={suggestions}
            onSelect={handleMentionSelect}
            onClose={closeDropdown}
            loading={loading}
          />
        )}
      </div>
      
      <div className="cbx-comment-input__footer">
        <span className={`cbx-comment-input__count ${getCountClass()}`}>
          {content.length}/1000
        </span>
        <motion.button
          type="submit"
          disabled={!content.trim() || submitting}
          className="cbx-comment-input__btn"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {submitting ? (
            "Posting..."
          ) : (
            <>
              <FiSend /> {buttonText}
            </>
          )}
        </motion.button>
      </div>
    </form>
  );
};

export default CommentInput;
